import {useState, useCallback, useMemo} from 'react';

import {GroupsRequester} from '../../Domain/Groups/GroupsRequester';
import {ContentRequester} from '../../Domain/Groups/ContentRequester';
import {buildNodeInfoMap} from './accessNodeHelpers';
import {buildSearchTreeData, buildSearchTree} from './searchTreeHelpers';

const collectSearchItemIds = (data) => {
    const ids = new Set();
    for (const r of data.results) {
        if (r.type === 'projects') ids.add(r.ProjectIdentifier);
        else if (r.type === 'folders') ids.add(r.FolderIdentifier);
        else if (r.type === 'procedures') ids.add(r.ProcedureIdentifier);
    }
    for (const p of data.parents.projects) ids.add(p.id);
    for (const f of data.parents.folders) ids.add(f.id);
    return ids;
};

const useAccessSearch = (workspace, {targetIds, inheritedIds, applyAccessEntries}) => {
    const [searchQuery, setSearchQuery] = useState('');
    const [searchData, setSearchData] = useState(null);

    const isSearchActive = searchQuery.length >= 2;

    const searchNodeInfoMap = useMemo(() => {
        if (!isSearchActive || !searchData?.results?.length) return new Map();
        const treeData = buildSearchTreeData(searchData);
        if (!treeData) return new Map();
        return buildNodeInfoMap(treeData.projects, treeData.childrenCache);
    }, [isSearchActive, searchData]);

    const fetchSearchAccess = useCallback(async (data) => {
        const allItemIds = collectSearchItemIds(data);
        if (!allItemIds.size || !targetIds.length) return;

        const searchIds = [...targetIds, ...inheritedIds];
        const {entries} = await GroupsRequester.getAccessForItems(workspace, {
            itemIds: [...allItemIds],
            searchIds,
        });
        applyAccessEntries(entries);
    }, [workspace, targetIds, inheritedIds, applyAccessEntries]);

    const handleSearch = useCallback(async (query) => {
        setSearchQuery(query);
        if (query.length < 2) {
            setSearchData(null);
            return;
        }
        try {
            const data = await ContentRequester.searchContent(workspace, query);
            setSearchData(data);
            await fetchSearchAccess(data);
        } catch (error) {
            console.error('Failed to search content:', error);
            setSearchData(null);
        }
    }, [workspace, fetchSearchAccess]);

    const searchTree = useMemo(() => {
        if (!isSearchActive) return null;
        return buildSearchTree(searchData);
    }, [isSearchActive, searchData]);

    const resetSearch = useCallback(() => {
        setSearchQuery('');
        setSearchData(null);
    }, []);

    return {
        searchQuery, isSearchActive, searchNodeInfoMap,
        handleSearch, searchTree, resetSearch,
    };
};

export default useAccessSearch;
